import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle, XCircle, Hourglass, ArrowLeft } from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("polling"); // polling | success | failed | expired
  const [details, setDetails] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    let attempts = 0;
    const maxAttempts = 10;
    const interval = 2500;
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      if (attempts >= maxAttempts) {
        setState("expired");
        return;
      }
      attempts++;
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        setDetails(res.data);
        if (res.data.payment_status === "paid") {
          setState("success");
          return;
        }
        if (res.data.status === "expired") {
          setState("expired");
          return;
        }
        setTimeout(poll, interval);
      } catch (e) {
        console.error(e);
        setTimeout(poll, interval);
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6 py-12"
      data-testid="payment-success-page"
    >
      <div className="max-w-lg w-full card-highlight p-10 text-center">
        {state === "polling" && (
          <>
            <Hourglass
              size={56}
              weight="duotone"
              className="text-gold mx-auto mb-5"
            />
            <h1 className="text-3xl text-white font-light mb-3">
              Betaling controleren...
            </h1>
            <p className="text-silver">
              We bevestigen je betaling bij Stripe. Eén moment.
            </p>
          </>
        )}
        {state === "success" && (
          <>
            <CheckCircle
              size={64}
              weight="duotone"
              className="text-gold mx-auto mb-5"
            />
            <div className="overline mb-3">Bevestigd</div>
            <h1 className="text-3xl md:text-4xl text-white font-light mb-4">
              Welkom aan boord!
            </h1>
            <p className="text-silver mb-2">
              Je betaling is gelukt. We nemen zo snel mogelijk contact met je op
              om je eerste rijles in te plannen.
            </p>
            {details && (
              <div className="text-sm text-silver mt-6 border-t border-white/10 pt-6">
                Bedrag: €{(details.amount_total / 100).toFixed(2)}{" "}
                {details.currency?.toUpperCase()}
                <br />
                Pakket: {details.package_id || "—"}
              </div>
            )}
            <Link
              to="/"
              className="btn-gold mt-8 inline-flex"
              data-testid="success-back-btn"
            >
              <ArrowLeft size={16} weight="bold" /> Terug naar home
            </Link>
          </>
        )}
        {(state === "failed" || state === "expired") && (
          <>
            <XCircle
              size={56}
              weight="duotone"
              className="text-red-400 mx-auto mb-5"
            />
            <h1 className="text-3xl text-white font-light mb-3">
              Betaling niet voltooid
            </h1>
            <p className="text-silver mb-6">
              Het lijkt erop dat de betaling is verlopen of geannuleerd. Je kunt
              het opnieuw proberen of ons een WhatsApp sturen.
            </p>
            <Link
              to="/pakketten"
              className="btn-ghost inline-flex"
              data-testid="retry-checkout-btn"
            >
              <ArrowLeft size={16} weight="bold" /> Probeer opnieuw
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
